import { logger } from "@/utils/logger";

/**
 * Utility functions for retry and timeout logic
 */

export const DEFAULT_RETRY_COUNT = 3;
export const DEFAULT_RETRY_DELAY_MS = 1000;
export const DEFAULT_TIMEOUT_MS = 30000; // 30s timeout

/**
 * Wraps a function with retry logic
 * @param fn The function to retry
 * @param maxRetries Maximum number of retry attempts
 * @param baseDelayMs Base delay in milliseconds between retries (increases with backoff)
 * @param operationName Name of the operation for logging
 * @returns Result of the function
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries = DEFAULT_RETRY_COUNT,
  baseDelayMs = DEFAULT_RETRY_DELAY_MS,
  operationName = "operation"
): Promise<T> {
  let lastError: any;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      if (attempt > 0) {
        logger.info(`Retry attempt ${attempt}/${maxRetries} for ${operationName}...`);
      }
      
      // Execute the function with timeout
      return await withTimeout(fn, DEFAULT_TIMEOUT_MS, operationName);
    } catch (error: any) {
      lastError = error;
      logger.error(`Attempt ${attempt}/${maxRetries} failed for ${operationName}:`, error);
      
      // If this is a timeout error and we're doing a search operation, 
      // don't retry as it's likely to time out again
      if (error?.message?.includes('timed out') && 
          operationName?.toLowerCase().includes('search') && 
          attempt > 0) {
        logger.info(`Search operation timed out and has been attempted ${attempt} times. Not retrying further.`);
        throw error;
      }
      
      if (attempt < maxRetries) {
        // Calculate delay with exponential backoff and jitter
        const jitter = Math.random() * 0.3 + 0.85; // Random value between 0.85 and 1.15
        const delay = Math.min(baseDelayMs * Math.pow(1.5, attempt) * jitter, 10000); // Cap at 10 seconds
        logger.info(`Waiting ${Math.round(delay)}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  // If we get here, all attempts failed
  throw lastError;
}

/**
 * Wraps a function with a timeout
 * @param fn The function to execute
 * @param timeoutMs Timeout in milliseconds
 * @param operationName Name of the operation for logging
 * @returns Result of the function
 */
export async function withTimeout<T>(
  fn: () => Promise<T>,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  operationName = "operation"
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    // Create a unique timeout ID for debugging
    const timeoutId = setTimeout(() => {
      reject({
        code: 'unknown',
        message: `Operation ${operationName} timed out after ${timeoutMs}ms`,
        details: 'No details',
        isTimeout: true
      });
    }, timeoutMs);
    
    fn().then(
      result => {
        clearTimeout(timeoutId);
        resolve(result);
      },
      error => {
        clearTimeout(timeoutId);
        reject(error);
      }
    );
  });
}

/**
 * Client-side retry helper that uses a counter to prevent excessive retries
 */
export async function withClientRetry<T>(
  fn: () => Promise<T>,
  maxRetries = 1, // Reduced retries for search operations to prevent excessive timeouts
  baseDelayMs = 500, // Faster retries for client operations
  operationName = "clientOperation"
): Promise<T> {
  return withRetry(fn, maxRetries, baseDelayMs, operationName);
}

/**
 * Creates a retryable version of any function
 * @param fn The function to make retryable
 * @param maxRetries Maximum number of retry attempts
 * @param baseDelayMs Base delay in milliseconds between retries
 * @returns A wrapped version of the function with retry logic
 */
export function createRetryableFunction<T, Args extends any[]>(
  fn: (...args: Args) => Promise<T>,
  maxRetries = DEFAULT_RETRY_COUNT,
  baseDelayMs = DEFAULT_RETRY_DELAY_MS
): (...args: Args) => Promise<T> {
  return async (...args: Args): Promise<T> => {
    return withRetry(
      () => fn(...args),
      maxRetries,
      baseDelayMs,
      fn.name || 'retryableFunction'
    );
  };
}
